import { useState, useEffect } from 'react';
import {
  ShieldExclamationIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  ClipboardDocumentListIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  UserIcon,
  ComputerDesktopIcon,
  DocumentIcon,
  PhoneIcon,
  ArrowDownTrayIcon,
  DocumentTextIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import FixItAllPanel from '../../components/remediation/FixItAllPanel';
import { remediationAPI, reportsAPI } from '../../services/api';

const SEVERITY_CONFIG = {
  critical: {
    label: 'Critical',
    icon: ShieldExclamationIcon,
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    borderColor: 'border-red-200 dark:border-red-800',
    textColor: 'text-red-700 dark:text-red-400',
    badgeColor: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  },
  alert: {
    label: 'Alert',
    icon: ExclamationCircleIcon,
    bgColor: 'bg-orange-50 dark:bg-orange-900/20',
    borderColor: 'border-orange-200 dark:border-orange-800',
    textColor: 'text-orange-700 dark:text-orange-400',
    badgeColor: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  },
  warning: {
    label: 'Warning',
    icon: ExclamationTriangleIcon,
    bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
    borderColor: 'border-yellow-200 dark:border-yellow-800',
    textColor: 'text-yellow-700 dark:text-yellow-400',
    badgeColor: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  },
  task: {
    label: 'Task',
    icon: ClipboardDocumentListIcon,
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    borderColor: 'border-blue-200 dark:border-blue-800',
    textColor: 'text-blue-700 dark:text-blue-400',
    badgeColor: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  },
};

const ENTITY_ICONS = {
  user: UserIcon,
  device: ComputerDesktopIcon,
  file: DocumentIcon,
};

function RemediationCard({ item, onRequestHelp }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const severityConfig = SEVERITY_CONFIG[item.severity] || SEVERITY_CONFIG.task;
  const SeverityIcon = severityConfig.icon;
  const EntityIcon = ENTITY_ICONS[item.affectedEntity?.type] || DocumentIcon;

  return (
    <div
      className={`rounded-lg border ${severityConfig.borderColor} ${severityConfig.bgColor} overflow-hidden`}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className={`flex-shrink-0 p-2 rounded-lg ${severityConfig.bgColor}`}>
            <SeverityIcon className={`h-5 w-5 ${severityConfig.textColor}`} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${severityConfig.badgeColor}`}
              >
                {severityConfig.label}
              </span>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {item.category}
              </span>
              {item.provider && (
                <span className="text-xs text-slate-400 dark:text-slate-500">
                  {item.provider === 'microsoft' ? 'Microsoft 365' : 'Google Workspace'}
                </span>
              )}
            </div>

            <h3 className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">
              {item.title}
            </h3>

            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              {item.description}
            </p>

            {item.affectedEntity && (
              <div className="mt-2 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                <EntityIcon className="h-4 w-4" />
                <span>{item.affectedEntity.name}</span>
                {item.affectedEntity.email && (
                  <span className="text-slate-400">({item.affectedEntity.email})</span>
                )}
              </div>
            )}
          </div>

          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex-shrink-0 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
          >
            {isExpanded ? (
              <ChevronUpIcon className="h-5 w-5" />
            ) : (
              <ChevronDownIcon className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
          <h4 className="text-sm font-medium text-slate-900 dark:text-white mb-2">
            How to Fix
          </h4>
          <div className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-line bg-slate-50 dark:bg-slate-900 rounded-lg p-4">
            {item.howToFix}
          </div>

          <div className="mt-4 flex items-center justify-between">
            <span className="text-xs text-slate-400">
              Detected: {new Date(item.detectedAt).toLocaleDateString()}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onRequestHelp(item)}
            >
              <PhoneIcon className="h-4 w-4 mr-1" />
              Request Professional Help
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCards({ summary }) {
  const cards = [
    {
      label: 'Critical',
      count: summary.bySeverity.critical,
      color: 'text-red-600 dark:text-red-400',
      bgColor: 'bg-red-50 dark:bg-red-900/20',
    },
    {
      label: 'Alerts',
      count: summary.bySeverity.alert,
      color: 'text-orange-600 dark:text-orange-400',
      bgColor: 'bg-orange-50 dark:bg-orange-900/20',
    },
    {
      label: 'Warnings',
      count: summary.bySeverity.warning,
      color: 'text-yellow-600 dark:text-yellow-400',
      bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
    },
    {
      label: 'Tasks',
      count: summary.bySeverity.task,
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {cards.map((card) => (
        <div
          key={card.label}
          className={`${card.bgColor} rounded-lg p-4 text-center`}
        >
          <p className={`text-3xl font-bold ${card.color}`}>{card.count}</p>
          <p className="text-sm text-slate-600 dark:text-slate-400">{card.label}</p>
        </div>
      ))}
    </div>
  );
}

function ProfessionalHelpModal({ item, isOpen, onClose, onSubmit }) {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onSubmit(item.id, { contactEmail: email, message });
      onClose();
    } catch (error) {
      console.error('Failed to submit:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />

        <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full p-6">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
            Request Professional Remediation
          </h2>

          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
            Our security experts can help you resolve: <strong>{item?.title}</strong>
          </p>

          <form onSubmit={handleSubmit} className="mt-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                Contact Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400"
                placeholder="your@email.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                Additional Details (Optional)
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                className="mt-1 block w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400"
                placeholder="Any additional context..."
              />
            </div>

            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting} className="flex-1">
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function RemediationPage() {
  const toast = useToast();
  const { tenant } = useAuth();
  const planSlug = tenant?.plan?.slug || 'starter';
  const isPro = ['pro', 'business', 'enterprise'].includes(planSlug);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [helpModalItem, setHelpModalItem] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [itemsRes, summaryRes] = await Promise.all([
        remediationAPI.getItems(),
        remediationAPI.getSummary(),
      ]);

      setItems(itemsRes.data.data || []);
      setSummary(summaryRes.data.data || null);
    } catch (err) {
      console.error('Error fetching remediation data:', err);
      setError('Failed to load remediation data');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestHelp = async (itemId, data) => {
    try {
      await remediationAPI.requestHelp(itemId, data);
      toast.success('Your request has been submitted. Our team will contact you shortly.');
    } catch (err) {
      toast.error('Failed to submit request. Please try again.');
    }
  };

  const filteredItems = filter === 'all'
    ? items
    : items.filter(item => item.severity === filter);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 dark:text-red-400">{error}</p>
        <Button onClick={fetchData} className="mt-4">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
          Remediation Center
        </h1>
        <p className="mt-1 text-slate-600 dark:text-slate-400">
          Security issues detected in your environment that need attention
        </p>
      </div>

      {summary && <SummaryCards summary={summary} />}

      {/* One-click automated remediation */}
      <FixItAllPanel />

      <Card>
        <div className="p-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              Remediation Items
            </h2>

            <div className="flex items-center gap-2">
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-1.5 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="all">All Severities</option>
                <option value="critical">Critical Only</option>
                <option value="alert">Alerts Only</option>
                <option value="warning">Warnings Only</option>
                <option value="task">Tasks Only</option>
              </select>

              <Button variant="outline" size="sm" onClick={fetchData}>
                Refresh
              </Button>
            </div>
          </div>
        </div>

        <div className="p-4">
          {filteredItems.length === 0 ? (
            <div className="text-center py-12">
              <ShieldExclamationIcon className="h-12 w-12 text-green-500 mx-auto" />
              <h3 className="mt-4 text-lg font-medium text-slate-900 dark:text-white">
                {filter === 'all' ? 'No Issues Detected' : 'No Items Match Filter'}
              </h3>
              <p className="mt-2 text-slate-600 dark:text-slate-400">
                {filter === 'all'
                  ? 'Great job! Your environment appears to be well configured.'
                  : 'Try selecting a different filter.'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredItems.map((item) => (
                <RemediationCard
                  key={item.id}
                  item={item}
                  onRequestHelp={setHelpModalItem}
                />
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Cyber Essentials Reference */}
      <Card>
        <div className="p-4">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
            Cyber Essentials v3.3 Controls
          </h3>
          <div className="grid gap-4 md:grid-cols-5">
            {[
              { name: 'User Access Control', icon: UserIcon },
              { name: 'Patch Management', icon: ComputerDesktopIcon },
              { name: 'Security Configuration', icon: DocumentIcon },
              { name: 'Malware Protection', icon: ShieldExclamationIcon },
              { name: 'Boundary Control', icon: ExclamationTriangleIcon },
            ].map((control) => (
              <div
                key={control.name}
                className="text-center p-3 rounded-lg bg-slate-50 dark:bg-slate-800"
              >
                <control.icon className="h-6 w-6 text-primary-600 mx-auto" />
                <p className="mt-2 text-xs font-medium text-slate-700 dark:text-slate-300">
                  {control.name}
                </p>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Reports & Export */}
      <Card>
        <div className="p-4">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
            Reports & Export
          </h3>
          <div className="grid gap-4 md:grid-cols-2">
            {/* PDF Report */}
            <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary-100 dark:bg-primary-900/30">
                  <DocumentTextIcon className="h-6 w-6 text-primary-600" />
                </div>
                <div>
                  <h4 className="font-medium text-slate-900 dark:text-white">
                    Cyber Essentials Report
                  </h4>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Branded PDF report for UK auditors
                  </p>
                </div>
              </div>
              {isPro ? (
                <Button
                  className="mt-4 w-full"
                  onClick={() => reportsAPI.downloadCEReport()}
                >
                  <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                  Download PDF Report
                </Button>
              ) : (
                <p className="mt-4 text-sm text-amber-600 dark:text-amber-400 text-center bg-amber-50 dark:bg-amber-900/20 rounded-lg py-2">
                  Upgrade to Pro to access PDF reports
                </p>
              )}
            </div>

            {/* CSV Exports */}
            <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                  <TableCellsIcon className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h4 className="font-medium text-slate-900 dark:text-white">
                    CSV Exports
                  </h4>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Export raw data for analysis
                  </p>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => reportsAPI.exportMFA()}
                >
                  MFA Status
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => reportsAPI.exportDevices()}
                >
                  Devices
                </Button>
                {isPro ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => reportsAPI.exportAlerts()}
                    >
                      Alerts
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => reportsAPI.exportFileSharing()}
                    >
                      File Sharing
                    </Button>
                  </>
                ) : (
                  <div className="col-span-2 text-sm text-amber-600 dark:text-amber-400 text-center bg-amber-50 dark:bg-amber-900/20 rounded-lg py-2">
                    Upgrade to Pro for Alerts & File Sharing exports
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Remediation Audit Log Export (M3) */}
          <div className="mt-4 p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/30">
                <ClipboardDocumentListIcon className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <h4 className="font-medium text-slate-900 dark:text-white">
                  Remediation Audit Log
                </h4>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Export the full remediation paper trail with a compliance rollup
                </p>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => remediationAPI.downloadAuditExport('csv')}
              >
                <TableCellsIcon className="h-4 w-4 mr-1.5" />
                Export CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => remediationAPI.downloadAuditExport('json')}
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-1.5" />
                Export JSON
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => remediationAPI.downloadAuditExport('pdf')}
              >
                <DocumentTextIcon className="h-4 w-4 mr-1.5" />
                Export PDF
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Professional Help Modal */}
      <ProfessionalHelpModal
        item={helpModalItem}
        isOpen={!!helpModalItem}
        onClose={() => setHelpModalItem(null)}
        onSubmit={handleRequestHelp}
      />
    </div>
  );
}
